import React, { useEffect, useRef, useState } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';

interface CityAutocompleteProps {
  value: string;
  onChange: (city: string) => void;
  /** id du champ (association label / focus). */
  id?: string;
}

interface Suggestion {
  text: string;
  placePrediction: google.maps.places.PlacePrediction;
}

const inputClass =
  'w-full bg-surface border border-line rounded-lg px-3 py-2.5 text-sm text-fg placeholder-gray-500 focus:outline-none focus:border-accent/60 transition-colors';

/**
 * Champ "Ville" avec autocomplétion Google Places via l'API moderne
 * `AutocompleteSuggestion` (compatible nouveaux comptes Google Cloud).
 * Réutilise le script Google Maps déjà chargé par l'APIProvider (App.tsx).
 * Nécessite l'API "Places API (New)" activée sur la clé.
 */
export const CityAutocomplete: React.FC<CityAutocompleteProps> = ({ value, onChange, id }) => {
  const placesLib = useMapsLibrary('places');
  const [query, setQuery] = useState(value);
  const [fetchedSuggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  // Requête trop courte : aucune suggestion affichée (dérivé, pas synchronisé)
  const suggestions = !placesLib || query.trim().length < 2 ? [] : fetchedSuggestions;

  // Fermer le menu au clic extérieur
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Récupérer les suggestions à chaque frappe
  useEffect(() => {
    if (!placesLib || query.trim().length < 2) return;

    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
        }
        const { suggestions: res } =
          await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: query,
            includedPrimaryTypes: ['(cities)'],
            includedRegionCodes: ['ch', 'fr'],
            sessionToken: sessionTokenRef.current,
          });
        if (cancelled) return;
        setSuggestions(
          res
            .filter((s) => s.placePrediction)
            .map((s) => ({
              text: s.placePrediction!.mainText?.text ?? s.placePrediction!.text.text,
              placePrediction: s.placePrediction!,
            }))
        );
        setOpen(true);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [placesLib, query]);

  const select = (s: Suggestion) => {
    setQuery(s.text);
    onChange(s.text);
    setSuggestions([]);
    setOpen(false);
    sessionTokenRef.current = null; // fin de session de facturation
  };

  return (
    <div className="relative" ref={wrapRef}>
      <input
        id={id}
        type="text"
        placeholder="Genève"
        value={query}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
        }}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        className={inputClass}
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-line bg-surface shadow-lg">
          {suggestions.map((s, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => select(s)}
                className="w-full min-h-11 px-3 py-2 text-left text-sm text-fg hover:bg-accent-strong transition-colors"
              >
                {s.placePrediction.text.text}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
